import httpService from '@/utils/httpService';
import store from '@/store';

/**
 * Wave C-ι demo slice — task↔CML link API wrappers.
 *
 * httpService.get/post/delete take a { url, data, headers } config object (NOT
 * positional args), mirroring useMonitoringTab.js. Kept as a focused module
 * rather than added to useMonitoringTab.js, which already exceeds 800 lines.
 *
 * Facility scoping (Option A): the task-side endpoints (cml/tml/reading options,
 * the task's cml-links, link, unlink) are TAG-scoped, not facility-scoped, because
 * a task is tag-centric — it should offer the CMLs of ITS tags, not every CML in a
 * facility. Server-side they are [IgnoreFacilityFilter], so they DON'T depend on
 * facility context at all. (Note: store.state.assetId IS typically set to the
 * facility on the task board — sourced from the route/workspace, not the
 * ?assetId= query param — so X-Facility-Id usually goes out; these endpoints just
 * ignore it. The header still matters for the CML→task direction opened from a
 * facility's Monitoring tab, where ListForCml IS facility-scoped.)
 * See [[project_ava_asset_facility_vocab]].
 */

/**
 * X-Facility-Id header when an asset is selected (it usually is on the task board —
 * store.state.assetId = the facility). Defensively omits the header if assetId is
 * ever absent, so the API is never handed an empty facility value. The task-side
 * endpoints are [IgnoreFacilityFilter] either way; this only matters for the
 * facility-scoped CML→task direction (Monitoring tab).
 * @returns {{ 'X-Facility-Id'?: string }}
 */
function facilityHeaders() {
  const assetId = store.state.assetId;
  return assetId ? { 'X-Facility-Id': assetId } : {};
}

const linksUrl = (cmlRecordId) => `/api/v1/cmls/${cmlRecordId}/task-links`;

/**
 * List task links for a CML (CML→task direction; called from the Monitoring tab).
 * @param {string} cmlRecordId
 * @returns {Promise<Array<{ id: string, taskId: string, taskName: string, tagId: string, cmlRecordId: string }>>}
 */
export async function listCmlTaskLinks(cmlRecordId) {
  const response = await httpService.get({ url: linksUrl(cmlRecordId), headers: facilityHeaders() });
  return response.data;
}

/**
 * Link a task to a CML (CML-level scope). TagId + FacilityId are derived server-side.
 * @param {string} cmlRecordId
 * @param {string} taskId
 * @returns {Promise<object>} the created link DTO
 */
export async function linkTaskToCml(cmlRecordId, taskId) {
  const response = await httpService.post({
    url: linksUrl(cmlRecordId),
    data: { taskId },
    headers: facilityHeaders(),
  });
  return response.data;
}

/**
 * Unlink (soft-delete) a task↔CML link.
 * @param {string} cmlRecordId
 * @param {string} linkId
 * @returns {Promise<void>}
 */
export async function unlinkCmlTask(cmlRecordId, linkId) {
  await httpService.delete({ url: `${linksUrl(cmlRecordId)}/${linkId}`, headers: facilityHeaders() });
}

/**
 * Tasks available to link (picker source). Uses the existing Tasks endpoint.
 * NOTE: confirm the response shape when wiring the picker — TasksController may
 * return a paged envelope or a bare list.
 * @returns {Promise<any>}
 */
export async function listTasks() {
  const response = await httpService.get({ url: '/api/Tasks' });
  return response.data;
}

/**
 * List the CMLs linked to a task (task side — Wave C-ι). Scoped by taskId only.
 * @param {string} taskId
 * @returns {Promise<Array<{ id: string, taskId: string, cmlRecordId: string, cmlNo: string }>>}
 */
export async function listTaskCmlLinks(taskId) {
  const response = await httpService.get({
    url: `/api/v1/tasks/${taskId}/cml-links`,
    headers: facilityHeaders(),
  });
  return response.data;
}

/**
 * CMLs available to link from the task-form picker, SCOPED BY the task's tags
 * (Option A). Returns [] when the task has no tags — there's nothing to scope to.
 * @param {string[]} tagIds — the task's linked Master Tag ids (selected.forgeTagsIds)
 * @returns {Promise<Array<{ id: string, cmlNo: string, cmlType: string }>>}
 */
export async function listCmlOptions(tagIds) {
  const ids = (tagIds || []).filter(Boolean);
  if (ids.length === 0) return [];
  const query = ids.map((id) => `tagIds=${encodeURIComponent(id)}`).join('&');
  const response = await httpService.get({
    url: `/api/v1/cml-task-links/cml-options?${query}`,
    headers: facilityHeaders(),
  });
  return response.data;
}

/**
 * TML points under a CML, for the scope builder's 2nd level (Wave C-ι narrowing).
 * Facility-agnostic (Option A) — works on the group task board. Returns [] for a
 * falsy cmlId so the caller can call it unconditionally.
 * @param {string} cmlId
 * @returns {Promise<Array<{ id: string, position: string }>>}
 */
export async function listTmlOptions(cmlId) {
  if (!cmlId) return [];
  const response = await httpService.get({
    url: `/api/v1/cml-task-links/tml-options?cmlId=${encodeURIComponent(cmlId)}`,
    headers: facilityHeaders(),
  });
  return response.data;
}

/**
 * Thickness readings under a TML point, for the scope builder's 3rd level
 * (Wave C-ι narrowing). Facility-agnostic (Option A).
 * @param {string} tmlPointId
 * @returns {Promise<Array<{ id: string, value: number, readingDate: string }>>}
 */
export async function listReadingOptions(tmlPointId) {
  if (!tmlPointId) return [];
  const response = await httpService.get({
    url: `/api/v1/cml-task-links/reading-options?tmlPointId=${encodeURIComponent(tmlPointId)}`,
    headers: facilityHeaders(),
  });
  return response.data;
}

/**
 * Link a task to a monitoring scope at ANY depth: CML-only, CML→TML, or
 * CML→TML→Reading. TagId + FacilityId are derived server-side from the CML; the
 * ascending invariant + parent-chain integrity are validated server-side. Sending
 * a null tmlPointId/thicknessReadingId requests the broader (CML-/TML-level) scope.
 * @param {string} cmlRecordId
 * @param {{ taskId: string, tmlPointId?: string|null, thicknessReadingId?: string|null }} scope
 * @returns {Promise<object>} the created link DTO
 */
export async function linkTaskScope(cmlRecordId, { taskId, tmlPointId, thicknessReadingId }) {
  const response = await httpService.post({
    url: linksUrl(cmlRecordId),
    data: {
      taskId,
      tmlPointId: tmlPointId || null,
      thicknessReadingId: thicknessReadingId || null,
    },
    headers: facilityHeaders(),
  });
  return response.data;
}
